import {
  defaultGuestOrderType,
  isGuestDeliveryOpen,
  isGuestOrderTypeEnabled,
  type GuestDeliveryConfig,
  type PlanFeaturesPayload,
} from './planFeatures';

const storageKey = (appName: string) => `peppar_guest_order_type:${appName}`;

export function parseGuestOrderType(raw: string | number | null | undefined): number | null {
  const n = typeof raw === 'number' ? raw : parseInt(String(raw ?? ''), 10);
  return n === 1 || n === 2 || n === 3 ? n : null;
}

export function getStoredGuestOrderType(appName?: string | null): number | null {
  if (!appName) {
    return null;
  }
  try {
    return parseGuestOrderType(sessionStorage.getItem(storageKey(appName)));
  } catch {
    return null;
  }
}

export function saveGuestOrderType(appName: string | undefined | null, orderType: number): void {
  if (!appName) {
    return;
  }
  const parsed = parseGuestOrderType(orderType);
  if (parsed == null) {
    return;
  }
  try {
    sessionStorage.setItem(storageKey(appName), String(parsed));
  } catch {
    // ignore quota / private mode
  }
}

function typeIsOpen(
  orderType: number,
  features: PlanFeaturesPayload | undefined,
  deliveryConfig?: GuestDeliveryConfig | null
): boolean {
  if (!isGuestOrderTypeEnabled(features, orderType)) {
    return false;
  }
  if (orderType === 2) {
    return isGuestDeliveryOpen(features, deliveryConfig);
  }
  return true;
}

/** URL type, then session storage for this restaurant, then plan default.
 * Table QR (`token`) always means dine-in and overrides a stored takeaway/delivery choice. */
export function resolveGuestOrderType(opts: {
  appName?: string | null;
  requested?: number | null;
  tableQr?: boolean;
  features?: PlanFeaturesPayload;
  deliveryConfig?: GuestDeliveryConfig | null;
}): number {
  if (opts.tableQr) {
    return 1;
  }
  const requested = parseGuestOrderType(opts.requested);
  if (requested != null && typeIsOpen(requested, opts.features, opts.deliveryConfig)) {
    return requested;
  }
  const stored = getStoredGuestOrderType(opts.appName);
  if (stored != null && typeIsOpen(stored, opts.features, opts.deliveryConfig)) {
    return stored;
  }
  return defaultGuestOrderType(opts.features, opts.deliveryConfig);
}

export function guestOrderTypeToService(orderType: number | null): 'dine' | 'takeaway' | 'delivery' | null {
  if (orderType === 1) return 'dine';
  if (orderType === 2) return 'delivery';
  if (orderType === 3) return 'takeaway';
  return null;
}

/** Append stored/explicit order type to a restaurant path unless it already has type=. */
export function withGuestOrderTypeQuery(
  pathname: string,
  appName?: string | null,
  orderType?: number | null
): string {
  if (!pathname) {
    return pathname;
  }
  const type = parseGuestOrderType(orderType) ?? getStoredGuestOrderType(appName);
  if (type == null || /[?&]type=/.test(pathname)) {
    return pathname;
  }
  return `${pathname}${pathname.includes('?') ? '&' : '?'}type=${type}`;
}
